import { chooseRecoverySource, SourceRecoveryDecision } from "./sourceRecovery";
import type { RankedPlaybackSource } from "./sourceRanking";

export type SourceGenerationState = {
  scopeKey: string;
  selectedSourceId: string;
  surfaceEpoch: number;
  lastReadyUri: string;
  activeSurfaceIdentity: string;
  pendingResumeSeconds: number | null;
  reloading: boolean;
  attemptedSourceIds: ReadonlySet<string>;
  recoverySourceId: string;
  manualSourceId: string;
  continuePausedSession: boolean;
};

export type BeginSourceGenerationOptions = {
  recovering: boolean;
  positionSeconds: number;
  continuePausedSession: boolean;
};

export type SourceReadyDecision = {
  state: SourceGenerationState;
  seekTarget?: number;
  continuePausedSession: boolean;
};

export type SourceRecoveryResult = SourceRecoveryDecision & {
  state: SourceGenerationState;
  stale: boolean;
};

const finitePosition = (seconds: number) =>
  Number.isFinite(seconds) ? Math.max(0, seconds) : 0;

export function createSourceGenerationState(
  scopeKey: string,
  explicitSourceId?: string,
): SourceGenerationState {
  return {
    scopeKey,
    selectedSourceId: explicitSourceId ?? "",
    surfaceEpoch: 0,
    lastReadyUri: "",
    activeSurfaceIdentity: "",
    pendingResumeSeconds: null,
    reloading: false,
    attemptedSourceIds: new Set<string>(),
    recoverySourceId: "",
    manualSourceId: explicitSourceId ?? "",
    continuePausedSession: false,
  };
}

export function resetSourceGenerationScope(
  state: SourceGenerationState,
  scopeKey: string,
  explicitSourceId?: string,
): SourceGenerationState {
  if (state.scopeKey === scopeKey) return state;
  return {
    ...createSourceGenerationState(scopeKey, explicitSourceId),
    surfaceEpoch: state.surfaceEpoch + 1,
  };
}

export function selectSourceGeneration(
  state: SourceGenerationState,
  selectedSourceId: string,
): SourceGenerationState {
  return { ...state, selectedSourceId };
}

export function bindSourceSurface(
  state: SourceGenerationState,
  uri?: string,
): { state: SourceGenerationState; identity: string } {
  const identity = `${state.surfaceEpoch}:${uri ?? "player-surface-empty"}`;
  return {
    state: { ...state, activeSurfaceIdentity: identity },
    identity,
  };
}

export function isAuthoritativeSourceSurface(
  state: SourceGenerationState,
  identity: string,
) {
  return state.activeSurfaceIdentity === identity;
}

export function beginSourceGeneration(
  state: SourceGenerationState,
  sourceId: string,
  options: BeginSourceGenerationOptions,
): SourceGenerationState {
  return {
    ...state,
    selectedSourceId: sourceId,
    surfaceEpoch: state.surfaceEpoch + 1,
    lastReadyUri: "",
    activeSurfaceIdentity: "",
    pendingResumeSeconds: finitePosition(options.positionSeconds),
    reloading: true,
    attemptedSourceIds: options.recovering
      ? state.attemptedSourceIds
      : new Set<string>(),
    recoverySourceId: options.recovering ? sourceId : "",
    manualSourceId: options.recovering ? state.manualSourceId : sourceId,
    continuePausedSession: options.continuePausedSession,
  };
}

export function retrySourceGeneration(
  state: SourceGenerationState,
  positionSeconds: number,
  continuePausedSession: boolean,
): SourceGenerationState {
  return {
    ...state,
    surfaceEpoch: state.surfaceEpoch + 1,
    lastReadyUri: "",
    activeSurfaceIdentity: "",
    pendingResumeSeconds: finitePosition(positionSeconds),
    reloading: true,
    attemptedSourceIds: new Set<string>(),
    recoverySourceId: "",
    continuePausedSession,
  };
}

export function prepareSourceTrackChange(
  state: SourceGenerationState,
  positionSeconds: number,
  onlyIfDecoderReady: boolean,
): { state: SourceGenerationState; prepared: boolean } {
  if (onlyIfDecoderReady && !state.lastReadyUri) {
    return { state, prepared: false };
  }
  return {
    state: {
      ...state,
      pendingResumeSeconds: finitePosition(positionSeconds),
      reloading: true,
    },
    prepared: true,
  };
}

export function resolveSourceReady(
  state: SourceGenerationState,
  uri: string | undefined,
  positionSeconds: number,
  initialResumeSeconds: number,
): SourceReadyDecision {
  const normalizedUri = uri ?? "";
  let seekTarget: number | undefined;
  let nextState = state;
  if (state.lastReadyUri !== uri) {
    const target = state.pendingResumeSeconds ?? (
      state.lastReadyUri
        ? finitePosition(positionSeconds)
        : finitePosition(initialResumeSeconds)
    );
    seekTarget = target > 0 ? target : undefined;
    nextState = {
      ...state,
      lastReadyUri: normalizedUri,
      pendingResumeSeconds: null,
      reloading: false,
    };
  }
  const continuePausedSession = nextState.continuePausedSession;
  return {
    state: { ...nextState, continuePausedSession: false },
    seekTarget,
    continuePausedSession,
  };
}

export function acceptSourceProgress(
  state: SourceGenerationState,
  seconds: number,
): { state: SourceGenerationState; accepted: boolean } {
  if (state.reloading) return { state, accepted: false };
  const position = finitePosition(seconds);
  if (
    state.pendingResumeSeconds !== null &&
    state.pendingResumeSeconds > 1 &&
    position < state.pendingResumeSeconds - 2
  ) {
    return { state, accepted: false };
  }
  return {
    state: { ...state, pendingResumeSeconds: null },
    accepted: true,
  };
}

export function markSourcePlaying(
  state: SourceGenerationState,
): SourceGenerationState {
  return { ...state, recoverySourceId: "" };
}

export function recoverSourceGeneration(
  state: SourceGenerationState,
  currentSourceId: string | undefined,
  rankedSources: RankedPlaybackSource[],
  enabled: boolean,
): SourceRecoveryResult {
  if (state.recoverySourceId && state.recoverySourceId !== currentSourceId) {
    return { state, stale: true, exhausted: false };
  }
  if (state.manualSourceId && state.manualSourceId === currentSourceId) {
    return {
      state: { ...state, recoverySourceId: "" },
      stale: false,
      exhausted: true,
    };
  }
  const attemptedSourceIds = new Set(state.attemptedSourceIds);
  if (currentSourceId) attemptedSourceIds.add(currentSourceId);
  const decision = enabled
    ? chooseRecoverySource(rankedSources, attemptedSourceIds, currentSourceId)
    : { candidate: undefined, exhausted: true };
  if (decision.candidate?.source.Id) {
    attemptedSourceIds.add(decision.candidate.source.Id);
  }
  return {
    ...decision,
    state: {
      ...state,
      attemptedSourceIds,
      recoverySourceId: "",
    },
    stale: false,
  };
}
